import { useMemo, useRef, useEffect, useState } from "react";
import { cn } from "@/utils/cn";
import { grcCss } from "@/utils/grcCss";
import type { GRCReportGridResponse, ProgramDetailDto, GRCActiveFilters } from "@/types/grc";

type SortDirection = "asc" | "desc";
type SortableColumn = "eventName" | "eventId" | "meetingPlanner" | "eventType";

interface SortConfig {
  column: SortableColumn;
  direction: SortDirection;
}

type SortState = Record<SortableColumn, SortDirection | null>;

// Sticky left-column widths (must match cumulative offsets below)
const COL_LINK   = 40;   // px
const COL_STATUS = 27;   // px
const COL_NAME   = 210;  // px
const COL_ID     = 58;   // px

// Non-sticky scrollable columns (after sticky ID)
const COL_MEETING_PLANNER = 128; // px
const COL_EVENT_TYPE      = 71;  // px

// Day and Total Rooms columns
const COL_DAY   = 33;  // px – matches Blazor 33.43 px
const COL_TOTAL = 50;  // px

// Cumulative sticky-left offsets
const OFFSET_STATUS = COL_LINK;
const OFFSET_NAME   = COL_LINK + COL_STATUS;
const OFFSET_ID     = COL_LINK + COL_STATUS + COL_NAME;

interface GRCGridProps {
  data: GRCReportGridResponse;
  filters: GRCActiveFilters;
  showPickups: boolean;
  showDatesAtBottom: boolean;
  highlightEventName?: string;
  onEventNameDoubleClick: (program: ProgramDetailDto) => void;
  onDraftEventClick: (program: ProgramDetailDto) => void;
}

export function GRCGridSkeleton() {
  const headerPlaceholders = Array.from({ length: 10 }, (_, idx) => `head-${idx + 1}`);
  const rowPlaceholders = Array.from({ length: 8 }, (_, idx) => `row-${idx + 1}`);

  return (
    <div className="overflow-x-auto border border-slate-200 bg-white" style={{ height: "calc(93vh - 160px)" }} aria-label="Loading GRC grid skeleton">
      <div className="w-full min-w-[1200px] animate-pulse">
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-[#E8E8E8] px-2 py-2">
          <div className="grid w-full grid-cols-[40px_27px_minmax(210px,1fr)_58px_128px_71px_repeat(10,minmax(33px,1fr))_50px] items-center gap-1">
            <div className="h-4 bg-slate-300" />
            <div className="h-4 bg-slate-300" />
            <div className="h-4 bg-slate-300" />
            <div className="h-4 bg-slate-300" />
            <div className="h-4 bg-slate-300" />
            <div className="h-4 bg-slate-300" />
            {headerPlaceholders.map((headerKey) => (
              <div key={headerKey} className="h-4 bg-slate-300" />
            ))}
            <div className="h-4 bg-slate-300" />
          </div>
        </div>

        <div className="space-y-1 px-2 py-2">
          {rowPlaceholders.map((rowKey) => (
            <div
              key={rowKey}
              className="grid w-full grid-cols-[40px_27px_minmax(210px,1fr)_58px_128px_71px_repeat(10,minmax(33px,1fr))_50px] items-center gap-1 py-1"
            >
              <div className="h-6 bg-slate-200" />
              <div className="h-6 bg-slate-200" />
              <div className="h-6 bg-slate-200" />
              <div className="h-6 bg-slate-200" />
              <div className="h-6 bg-slate-200" />
              <div className="h-6 bg-slate-200" />
              {headerPlaceholders.map((headerKey) => (
                <div key={`${rowKey}-${headerKey}`} className="h-6 bg-slate-200" />
              ))}
              <div className="h-6 bg-slate-300" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * GRC Room-Block Grid.
 *
 * Structure:
 *   ┌────────────────────────────────────────── (sticky left) ─┬─ (scrollable) ─────────────────────────────────────────────────────
 *   │ Link │ Status │ Event Name (210px) │ ID │ Meeting Planner (128px) │ Type (71px) │  day-1 │ day-2 │ … │ day-31 │ Total Rooms
 *   ├──────┴────────┴────────────────────┴────┼─────────────────────────
 *   │ …rows…                                  │
 *   ├─────────────────────────────────────────┤
 *   │ Blocked footer │ …per-day blocked counts│
 *   │ Available footer│ …per-day available     │
 *   └─────────────────────────────────────────┘
 */
export function GRCGrid({
  data,
  filters,
  showPickups,
  showDatesAtBottom,
  highlightEventName,
  onEventNameDoubleClick,
  onDraftEventClick
}: GRCGridProps) {
  const [sortState, setSortState] = useState<SortState>({
    eventName: null,
    eventId: null,
    meetingPlanner: null,
    eventType: null,
  });

  const activeSorts = useMemo(() => {
    const sorts: SortConfig[] = [];
    const order: SortableColumn[] = ["eventName", "eventId", "meetingPlanner", "eventType"];
    for (const col of order) {
      if (sortState[col]) {
        sorts.push({ column: col, direction: sortState[col]! });
      }
    }
    return sorts;
  }, [sortState]);

  const sortedAndFilteredPrograms = useMemo(() => {
    let programs = data.programDetails.filter((p) => {
      if (filters.eventTypes.length > 0 && !filters.eventTypes.includes(p.eventType)) return false;
      if (filters.statuses.length > 0 && !filters.statuses.includes(p.eventStatus)) return false;
      return true;
    });

    if (activeSorts.length === 0) return programs;

    return [...programs].sort((a, b) => {
      for (const sort of activeSorts) {
        let aVal: string | number;
        let bVal: string | number;

        switch (sort.column) {
          case "eventName":
            aVal = a.eventName.toLowerCase();
            bVal = b.eventName.toLowerCase();
            break;
          case "eventId":
            aVal = a.eventId.toLowerCase();
            bVal = b.eventId.toLowerCase();
            break;
          case "meetingPlanner":
            aVal = (a.meetingPlanner || "").toLowerCase();
            bVal = (b.meetingPlanner || "").toLowerCase();
            break;
          case "eventType":
            aVal = a.eventType.toLowerCase();
            bVal = b.eventType.toLowerCase();
            break;
        }

        if (aVal < bVal) {
          return sort.direction === "asc" ? -1 : 1;
        }
        if (aVal > bVal) {
          return sort.direction === "asc" ? 1 : -1;
        }
      }
      return 0;
    });
  }, [data.programDetails, filters, activeSorts]);

  const filteredPrograms = sortedAndFilteredPrograms;

  const handleColumnSort = (column: SortableColumn) => {
    setSortState((prev) => {
      const currentDirection = prev[column];
      const newDirection: SortDirection | null =
        currentDirection === null ? "asc" : currentDirection === "asc" ? "desc" : null;

      return {
        ...prev,
        [column]: newDirection,
      };
    });
  };

  // Index of the first row matching the highlight — used for scroll-into-view
  const firstHighlightIdx = useMemo(() => {
    if (!highlightEventName) return -1;
    return filteredPrograms.findIndex((p) =>
      p.eventName.toLowerCase().includes(highlightEventName.toLowerCase())
    );
  }, [filteredPrograms, highlightEventName]);

  const firstHighlightRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (firstHighlightIdx >= 0 && firstHighlightRef.current) {
      firstHighlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [firstHighlightIdx]);

  const days = data.daysOfMonth;

  if (filteredPrograms.length === 0 && days.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-slate-500">No data available for this month.</p>
    );
  }

  return (
    <div className="overflow-auto" style={{ height: "calc(93vh - 160px)" }}>
      <table className=" text-xs" aria-label="GRC room block grid" style={{ width: "100%" }}>
        <thead className="sticky top-0 z-30 bg-[#E8E8E8]">
          <tr>
            {/* ── Sticky left columns ── */}
            <StickyTh width={COL_LINK} left={0} className="bg-[#E8E8E8]" />
            <StickyTh width={COL_STATUS} left={OFFSET_STATUS} className="bg-[#E8E8E8] text-center">
              --
            </StickyTh>
            <StickyTh width={COL_NAME} left={OFFSET_NAME} className="bg-[#E8E8E8] text-left pl-1 font-semibold text-black p-0">
              <SortableHeader
                label="Event Name"
                column="eventName"
                sortDirection={sortState.eventName}
                onClick={() => handleColumnSort("eventName")}
              />
            </StickyTh>
            <StickyTh width={COL_ID} left={OFFSET_ID} className="bg-[#E8E8E8] text-center font-semibold text-black p-0">
              <SortableHeader
                label="ID"
                column="eventId"
                sortDirection={sortState.eventId}
                onClick={() => handleColumnSort("eventId")}
              />
            </StickyTh>

            {/* ── Non-sticky scrollable columns ── */}
            <th
              scope="col"
              className="w-[128px] min-w-[128px] border-l-[0.5px] border-slate-300 bg-[#E8E8E8] px-0 py-0 text-center font-semibold text-black"
            >
              <SortableHeader
                label="Meeting Planner"
                column="meetingPlanner"
                sortDirection={sortState.meetingPlanner}
                onClick={() => handleColumnSort("meetingPlanner")}
              />
            </th>
            <th
              scope="col"
              className="w-[71px] min-w-[71px] border-l-[0.5px] border-slate-300 bg-[#E8E8E8] px-0 py-0 text-center font-semibold text-black"
            >
              <SortableHeader
                label="Type"
                column="eventType"
                sortDirection={sortState.eventType}
                onClick={() => handleColumnSort("eventType")}
              />
            </th>

            {/* ── Day columns ── */}
            {days.map((d) => {
              const [dayShort = "", dayNum = ""] = d.display.split(" ");
              return (
                <th
                  key={d.day}
                  scope="col"
                  className={cn(
                    "w-[33px] min-w-[33px] border-l-[0.5px] border-slate-300 bg-[#E8E8E8] text-center font-normal",
                    grcCss(d.holidayHeaderCSSClass)
                  )}
                  title={d.isHoliday ? d.holidayName : undefined}
                >
                  <div className="flex flex-col items-center justify-center leading-none">
                    <span className="text-[10px] font-semibold leading-[15px] text-[#75787B]">{dayShort}</span>
                    {d.isToday ? (
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-black text-white">
                        <b className="text-[13px] leading-none">{dayNum}</b>
                      </span>
                    ) : (
                      <b className="text-[13px] leading-none">{dayNum}</b>
                    )}
                  </div>
                </th>
              );
            })}

            {/* Total Rooms – sticky right */}
            <th
              scope="col"
              className="sticky right-0 z-20 w-[50px] min-w-[50px] border-l-[0.5px] border-slate-300 bg-[#E8E8E8] px-1 py-1 text-center text-[10px] font-semibold leading-tight text-black"
            >
              Total Rooms
            </th>
          </tr>
        </thead>

        <tbody>
          {filteredPrograms.map((prog, idx) => {
            const isHighlighted =
              !!highlightEventName &&
              prog.eventName.toLowerCase().includes(highlightEventName.toLowerCase());

            return (
              <tr
                key={prog.softHoldId > 0 ? `${prog.isSoftHold ? "sh" : "dr"}-${prog.softHoldId}` : `ev-${idx}`}
                ref={idx === firstHighlightIdx ? firstHighlightRef : undefined}
                className={cn(
                  "h-[35px] border-b border-slate-100",
                  isHighlighted
                    ? "[&>td]:!bg-[lightyellow] font-bold [text-shadow:0_0_1px_rgba(0,0,0,0.3)] outline outline-2 outline-amber-400"
                    : idx % 2 === 0
                    ? "bg-white [&>.sticky-col]:bg-white"
                    : "bg-[#F2F2F2] [&>.sticky-col]:bg-[#F2F2F2]"
                )}
              >
                {/* Link status icon */}
                <StickyTd width={COL_LINK} left={0}>
                  <LinkStatusIcon prog={prog} />
                </StickyTd>

                {/* Event status dot */}
                <StickyTd width={COL_STATUS} left={OFFSET_STATUS} className="text-center">
                  <span
                    className={cn("inline-block h-3 w-3 rounded-[2px]", grcCss(prog.eventStatusCSSClass))}
                    title={prog.eventStatus}
                    aria-label={prog.eventStatus}
                  />
                </StickyTd>

                {/* Event name */}
                <StickyTd width={COL_NAME} left={OFFSET_NAME} className="max-w-[210px] pl-1">
                  <button
                    type="button"
                    className={cn(
                      "w-full truncate bg-transparent text-left",
                      "cursor-default select-none",
                      (prog.isSoftHold || prog.isDraft) && "cursor-pointer"
                    )}
                    title={prog.eventName}
                    onDoubleClick={() => onEventNameDoubleClick(prog)}
                    onKeyDown={(e) => {
                      if (prog.isDraft && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        onDraftEventClick(prog);
                      }
                    }}
                  >
                    <span className="block truncate text-slate-900">{prog.eventName}</span>
                    {prog.isSoftHold && prog.softHoldCreatedDate && (
                      <span className="block text-[10px] text-slate-500">
                        Added: {formatGridDate(prog.softHoldCreatedDate)} by {prog.organizerName}
                      </span>
                    )}
                  </button>
                </StickyTd>

                {/* Event ID */}
                <StickyTd width={COL_ID} left={OFFSET_ID} className="text-center">
                  <span className="truncate">{prog.eventId}</span>
                </StickyTd>

                {/* Meeting Planner */}
                <td
                  className="w-[128px] min-w-[128px] border-l-[0.5px] border-slate-300 px-1 py-1 text-center text-[13px] text-slate-900"
                >
                  {prog.meetingPlanner || "-"}
                </td>

                {/* Event Type */}
                <td
                  className="w-[71px] min-w-[71px] border-l-[0.5px] border-slate-300 px-0.5 py-1 text-center text-[13px] text-slate-900"
                >
                  {prog.eventType}
                </td>

                {/* Room block cells */}
                {days.map((d) => {
                  const block = prog.roomBlockDtos[String(d.day)];
                  const blockedValue = block?.blocked ?? 0;
                  const nightGuestsPickupValue = block?.nightGuestsPickup ?? 0;
                  // Holiday background on the cell; softhold class layers on top
                  const holidayBg = grcCss(d.holidayCSSClass);
                  const softHoldBg = block?.cellCssForSoftHold ? grcCss(block.cellCssForSoftHold) : "";

                  if (showPickups && prog.totalNightGuestsPickup > 0) {
                    return (
                      <td
                        key={d.day}
                        className={cn(
                          "w-[33px] min-w-[33px] border-l-[0.5px] border-slate-300  text-center",
                          holidayBg,
                          softHoldBg
                        )}
                      >
                        <div className="grid">
                          <span className="text-slate-800">{blockedValue > 0 ? blockedValue : "-"}</span>
                          <span className="text-[11px] text-slate-500 " style={{ backgroundColor: "#FFFBED", borderTop: "1px solid #E8E8E8" }}>
                            {nightGuestsPickupValue > 0 ? nightGuestsPickupValue : "-"}
                          </span>
                        </div>
                      </td>
                    );
                  }

                  return (
                    <td
                      key={d.day}
                      className={cn(
                        "w-[33px] min-w-[33px] border-l-[0.5px] border-slate-300 py-1 text-center",
                        holidayBg,
                        softHoldBg,
                        !block?.blocked && !holidayBg && "text-slate-300"
                      )}
                    >
                      {blockedValue > 0 ? blockedValue : "-"}
                    </td>
                  );
                })}

                {/* Total Rooms – sticky right */}
                <td className="sticky right-0 z-10 w-[50px] min-w-[50px] border-l-[0.5px] border-slate-300 bg-[#A7A8AA] px-1 py-1 text-center font-semibold">
                  {showPickups && prog.totalNightGuestsPickup > 0 ? (
                    <div className="grid">
                      <span>{prog.totals > 0 ? prog.totals : "-"}</span>
                      <span className="text-[11px]">
                        {prog.totalNightGuestsPickup > 0 ? prog.totalNightGuestsPickup : ""}
                      </span>
                    </div>
                  ) : (
                    <span>{prog.totals > 0 ? prog.totals : "-"}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>

        <tfoot className="sticky bottom-0 z-20 border-t border-slate-400 text-[11px] font-semibold shadow-[0_-1px_0_0_#94A3B8]">
          {/* Dates at bottom row – bg #E8E8E8, black text */}
          {showDatesAtBottom && (
            <tr className="bg-[#E8E8E8] text-black [&>.sticky-col]:bg-[#E8E8E8]">
              <StickyTd width={COL_LINK} left={0} />
              <StickyTd width={COL_STATUS} left={OFFSET_STATUS} />
              <StickyTd width={COL_NAME} left={OFFSET_NAME} />
              <StickyTd width={COL_ID} left={OFFSET_ID} />
              <td className="h-[42px] w-[128px] min-w-[128px] border-l border-l-[0.5px]" />
              <td className="h-[42px] w-[71px] min-w-[71px] border-l border-l-[0.5px]" />
              {days.map((d) => {
                const [dayShort = "", dayNumber = ""] = d.display.split(" ");
                return (
                  <td
                    key={d.day}
                    className={cn(
                      "h-[42px] w-[33px] min-w-[33px] border-l border-l-[0.5px]  text-center",
                      grcCss(d.holidayHeaderCSSClass)
                    )}
                  >
                    <div className="flex h-full flex-col items-center justify-center leading-none">
                      <span className="text-[10px] font-semibold text-[#75787B]">{dayShort}</span>
                      <b className="text-[13px] leading-none">{dayNumber}</b>
                    </div>
                  </td>
                );
              })}
              <td className="sticky right-0 z-10 h-[42px] w-[50px] min-w-[50px] border-l border-l-[0.5px] border-[#BBBCBC] bg-[#E8E8E8]" />
            </tr>
          )}

          {/* Blocked row – bg black, white text */}
          <tr className="bg-black text-white [&>.sticky-col]:bg-black">
            <StickyTd width={COL_LINK} left={0} />
            <StickyTd width={COL_STATUS} left={OFFSET_STATUS} />
            <StickyTd width={COL_NAME} left={OFFSET_NAME} className="text-right pr-1.5 text-[11px]">
              Blocked
            </StickyTd>
            <StickyTd width={COL_ID} left={OFFSET_ID} />
            <td className="w-[128px] min-w-[128px] border-l border-l-[0.5px] border-slate-700" />
            <td className="w-[71px] min-w-[71px] border-l border-l-[0.5px] border-slate-700" />
            {days.map((d) => (
              <td
                key={d.day}
                className={cn("h-[25px] py-1 text-center", grcCss(d.totalBlockedCountCss))}
              >
                {d.totalBlockedCount > 0 ? d.totalBlockedCount : "-"}
              </td>
            ))}
            <td className="sticky right-0 z-10 w-[50px] min-w-[50px] border-l border-l-[0.5px] border-slate-700 bg-black py-1 text-center text-white">
              {data.sumOfOtalRooms > 0 ? data.sumOfOtalRooms : "-"}
            </td>
          </tr>

          {/* Pickup rows – shown only when showPickups */}
          {showPickups && (
            <>
              <FooterLabelRow
                label="Night Guest Pickup"
                rowClassName="bg-[#FFFBED] text-black"
                stickyBgClass="bg-[#FFFBED]"
                days={days}
                getValue={(d) => d.totalNightGuestsPickup}
                rowTotal={data.sumOfTotalNightGuestsPickup}
              />
              <FooterLabelRow
                label="Day Guests"
                rowClassName="bg-[#DFDFDF] text-black"
                stickyBgClass="bg-[#DFDFDF]"
                days={days}
                getValue={(d) => d.totalDayGuestsCount}
                rowTotal={data.sumOfTotalDayGuestsPickUp}
              />
              <FooterLabelRow
                label="Offsite Guests"
                rowClassName="bg-[#F3F3F3] text-black"
                stickyBgClass="bg-[#F3F3F3]"
                days={days}
                getValue={(d) => d.totalOffSiteGuestsCount}
                rowTotal={data.sumOfTotalOffSiteGuestsPickUp}
              />
              <FooterLabelRow
                label="Pickup Total"
                rowClassName="bg-[#53565A] text-white"
                stickyBgClass="bg-[#53565A]"
                days={days}
                getValue={(d) => d.totalPickUpCount}
                rowTotal={data.sumOfTotalPickUpCounts}
              />
            </>
          )}

          {/* Available to sell – bg #A7A8AA, black text */}
          <tr className="bg-[#A7A8AA] text-black [&>.sticky-col]:bg-[#A7A8AA]">
            <StickyTd width={COL_LINK} left={0} />
            <StickyTd width={COL_STATUS} left={OFFSET_STATUS} />
            <StickyTd width={COL_NAME} left={OFFSET_NAME} className="pl-1">
              Available to sell{" "}
              <span className="font-normal">(including soft holds)</span>
            </StickyTd>
            <StickyTd width={COL_ID} left={OFFSET_ID} />
            <td className="w-[128px] min-w-[128px] border-l border-l-[0.5px] border-slate-300" />
            <td className="w-[71px] min-w-[71px] border-l border-l-[0.5px] border-slate-300" />
            {days.map((d) => (
              <td
                key={d.day}
                className={cn("h-[25px] py-1 text-center", grcCss(d.availableToSellcss))}
              >
                {d.availableToSell }
              </td>
            ))}
            <td className="sticky right-0 z-10 w-[50px] min-w-[50px] border-l border-l-[0.5px] border-slate-300 bg-[#A7A8AA] py-1 text-center">-</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

/** Reformats an ISO date string (YYYY-MM-DD[T…]) to MM-dd-yyyy.
 * Date components are extracted via regex — no Date object is constructed,
 * so there is zero risk of a UTC↔local timezone shift causing an off-by-one-day bug.
 */
export function formatGridDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  // Rearrange from YYYY-MM-DD to MM-dd-YYYY
  return `${match[2]}-${match[3]}-${match[1]}`;
}

interface StickyThProps {
  width: number;
  left: number;
  className?: string;
  children?: React.ReactNode;
}

function StickyTh({ width, left, className, children }: StickyThProps) {
  return (
    <th
      scope="col"
      className={cn("sticky z-40", left > 0 && "border-l-[0.5px] border-slate-300", className)}
      style={{ width, minWidth: width, left }}
    >
      {children}
    </th>
  );
}

interface StickyTdProps {
  width: number;
  left: number;
  className?: string;
  children?: React.ReactNode;
}

function StickyTd({ width, left, className, children }: StickyTdProps) {
  return (
    <td
      className={cn("sticky-col sticky z-10 bg-white py-1", left > 0 && "border-l-[0.5px] border-slate-300", className)}
      style={{ width, minWidth: width, left }}
    >
      {children}
    </td>
  );
}

interface FooterLabelRowProps {
  label: string;
  rowClassName: string;
  stickyBgClass: string;
  days: GRCReportGridResponse["daysOfMonth"];
  getValue: (d: GRCReportGridResponse["daysOfMonth"][number]) => number;
  rowTotal: number;
}

function FooterLabelRow({ label, rowClassName, stickyBgClass, days, getValue, rowTotal }: FooterLabelRowProps) {
  return (
    <tr className={rowClassName}>
      <StickyTd width={COL_LINK} left={0} className={stickyBgClass} />
      <StickyTd width={COL_STATUS} left={OFFSET_STATUS} className={stickyBgClass} />
      <StickyTd width={COL_NAME} left={OFFSET_NAME} className={cn(stickyBgClass, "pl-1 text-[11px]")}>
        {label}
      </StickyTd>
      <StickyTd width={COL_ID} left={OFFSET_ID} className={stickyBgClass} />
      <td className="w-[128px] min-w-[128px] border-l border-l-[0.5px] border-slate-300" />
      <td className="w-[71px] min-w-[71px] border-l border-l-[0.5px] border-slate-300" />
      {days.map((d) => {
        const v = getValue(d);
        return (
          <td key={d.day} className="h-[25px] py-0.5 text-center text-[11px]">
            {v > 0 ? v : ""}
          </td>
        );
      })}
      <td className={cn("sticky right-0 z-10 w-[50px] min-w-[50px] border-l border-l-[0.5px] border-slate-300 py-0.5 text-center text-[11px]", stickyBgClass)}>
        {rowTotal > 0 ? rowTotal : ""}
      </td>
    </tr>
  );
}

/** SVG link-status icon matching Blazor's LinkStatusIcon component */
function LinkStatusIcon({ prog }: { prog: ProgramDetailDto }) {
  if (!prog.isSoftHold) return null;

  const hasEvent = prog.eventId && prog.eventId.length > 0;
  const isConfirmed = prog.isSoftHoldConfirmed;

  if (!hasEvent) {
    // Broken link — grey
    return (
      <svg
        viewBox="0 0 24 24"
        className="mx-auto h-4 w-4 text-slate-400"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        aria-label="Unlinked soft hold"
      >
        <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
        <line x1="2" y1="2" x2="22" y2="22" />
      </svg>
    );
  }

  // Linked — green if confirmed, black if not
  const color = isConfirmed ? "#358225" : "black";
  return (
    <svg
      viewBox="0 0 24 24"
      className="mx-auto h-4 w-4"
      fill="none"
      stroke={color}
      strokeWidth={2}
      aria-label={isConfirmed ? "Linked and confirmed" : "Linked, unconfirmed"}
    >
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  );
}

interface SortableHeaderProps {
  label: string;
  column: SortableColumn;
  sortDirection: SortDirection | null;
  onClick: () => void;
}

function SortableHeader({ label, column, sortDirection, onClick }: SortableHeaderProps) {
  const isLeft = column === "eventName";
  
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full h-full inline-flex items-center gap-1 px-1 py-1 hover:bg-slate-300 transition-colors cursor-pointer select-none",
        isLeft ? "justify-start" : "justify-center"
      )}
      title={`Click to sort by ${label}. Current: ${sortDirection ? sortDirection.toUpperCase() : "None"}`}
    >
      <span>{label}</span>
      {sortDirection && (
        <span className="text-xs font-bold" aria-label={`Sorted ${sortDirection}`}>
          {sortDirection === "asc" ? "↑" : "↓"}
        </span>
      )}
      {!sortDirection && (
        <span className="text-xs text-slate-400 opacity-50">⇅</span>
      )}
    </button>
  );
}
